import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, SIZES } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth, isAdmin } from "@/lib/auth";
import OrderModelBlock from "@/components/OrderModelBlock";
import { blankLine, groupBlocks, blockActions, toBlockLines, usedByOthers } from "@/lib/blocks";
import { detectKoli } from "@/lib/koli";
import { SoleWarning } from "@/components/SoleWarning";
import { Scissors, Plus, Pencil, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";


export default function Cutting() {
  const admin = isAdmin(useAuth().user);
  const { products, productById, productLabel, koliTypes } = useData();
  const [cutterName, setCutterName] = useState("");
  const location = useLocation();
  const [note, setNote] = useState(location.state?.note || "");
  const navigate = useNavigate();
  const prefill = location.state?.prefill;
  const [lines, setLines] = useState(() => (prefill?.length ? toBlockLines(prefill, productById, koliTypes) : [blankLine()]));
  const blocks = useMemo(() => groupBlocks(lines), [lines]);
  const usedModels = useMemo(() => usedByOthers(blocks), [blocks]);
  const acts = blockActions(setLines, products);
  const [history, setHistory] = useState([]);
  const [soleStock, setSoleStock] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadHistory = async () => {
    const [r, s] = await Promise.all([api.get("/cutting"), api.get("/soles/stock")]);
    setHistory(r.data);
    setSoleStock(s.data);
  };
  useEffect(() => { loadHistory(); }, []);
  // Prefilled lines may arrive before products load: attach their model codes once known
  useEffect(() => {
    if (!products.length) return;
    setLines((prev) => (prev.some((l) => l.product_id && !l.model)
      ? toBlockLines(prev.map(({ product_id, sizes }) => ({ product_id, sizes })), productById, koliTypes) : prev));
  }, [products.length, productById, koliTypes]);
  // Koli types may load after the prefill: detect each line's koli type and count once they arrive
  useEffect(() => {
    if (!koliTypes.length) return;
    setLines((prev) => (prev.some((l) => l.product_id && !l.koli_type_id && !l.koli_touched)
      ? prev.map((l) => (l.product_id && !l.koli_type_id ? { ...l, ...detectKoli(l.sizes, koliTypes), koli_touched: true } : l)) : prev));
  }, [koliTypes]);

  // Sole shortages per sole key, summed across all lines using the same sole
  const soleShort = (() => {
    const need = {};
    lines.forEach((l) => {
      const p = productById(l.product_id);
      if (!p?.sole_code) return;
      const k = `${p.sole_code}||${p.sole_color || ""}`;
      need[k] = need[k] || {};
      Object.entries(l.sizes).forEach(([s, q]) => { need[k][s] = (need[k][s] || 0) + (parseInt(q) || 0); });
    });
    const out = {};
    Object.entries(need).forEach(([k, sizes]) => {
      Object.entries(sizes).forEach(([s, q]) => {
        if (q <= 0) return;
        const row = soleStock.find((x) => `${x.sole_code}||${x.sole_color || ""}` === k && x.size === Number(s));
        // while editing, this cutting's own soles were already deducted — add them back
        const own = editingId ? editingItems.reduce((a, it) => {
          const p = productById(it.product_id);
          const ik = it.sole_code !== undefined ? `${it.sole_code}||${it.sole_color || ""}` : `${p?.sole_code}||${p?.sole_color || ""}`;
          return ik === k && it.size === Number(s) ? a + it.qty : a;
        }, 0) : 0;
        const avail = (row ? row.qty : 0) + own;
        if (q > avail) (out[k] = out[k] || []).push({ size: s, need: q, avail });
      });
    });
    return out;
  })();
  const soleWarningsFor = (l) => {
    const p = productById(l.product_id);
    return p?.sole_code ? { p, list: soleShort[`${p.sole_code}||${p.sole_color || ""}`] || [] } : null;
  };

  const updateLine = (id, patch) => setLines(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  const updateSize = (id, size, qty) =>
    setLines(prev => prev.map(l => l.id === id ? { ...l, koli_count: "", sizes: { ...l.sizes, [size]: qty } } : l));
  const addLine = () => setLines(prev => [...prev, blankLine()]);

  const totalPairs = lines.reduce((sum, l) =>
    sum + Object.values(l.sizes).reduce((a, b) => a + (parseInt(b) || 0), 0), 0);

  const submit = async () => {
    const items = [];
    for (const l of lines) {
      if (!l.model) { toast.error("Her model için model kodu seçin"); return; }
      if (!l.product_id) { toast.error(`${l.model}: her satır için renk seçin`); return; }
      for (const s of SIZES) {
        const q = parseInt(l.sizes[s]) || 0;
        if (q > 0) items.push({ product_id: l.product_id, size: s, qty: q });
      }
    }
    if (!items.length) { toast.error("En az bir numara için adet giriniz"); return; }
    const win = window.open("", "_blank");
    setSubmitting(true);
    try {
      let savedId = editingId;
      if (editingId) {
        await api.put(`/cutting/${editingId}`, { cutter_name: cutterName, note, items });
        toast.success("Kesim güncellendi — stok ve taban yeniden hesaplandı");
        setEditingId(null);
      } else {
        const r = await api.post("/cutting", { cutter_name: cutterName, note, items });
        savedId = r.data.id;
        toast.success(`${totalPairs} çift kesim stoğa eklendi`);
      }
      if (win) win.location.href = `/kesim-fis/${savedId}`;
      setLines([blankLine()]); setCutterName(""); setNote("");
      if (prefill) navigate(location.pathname, { replace: true, state: null });
      loadHistory();
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || "Hata oluştu");
    } finally { setSubmitting(false); }
  };

  const startEdit = (c) => {
    const byProduct = {};
    c.items.forEach((it) => {
      byProduct[it.product_id] = byProduct[it.product_id] || {};
      byProduct[it.product_id][it.size] = it.qty;
    });
    setLines(toBlockLines(Object.entries(byProduct).map(([product_id, sizes]) => ({ product_id, sizes })), productById, koliTypes));
    setCutterName(c.cutter_name || ""); setNote(c.note || "");
    setEditingId(c.id);
    setEditingItems(c.items);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setLines([blankLine()]); setCutterName(""); setNote(""); };

  const removeCutting = async (id) => {
    if (!window.confirm("Bu kesim silinsin mi? Stoktan düşülecek ve tabanlar geri eklenecek.")) return;
    try {
      await api.delete(`/cutting/${id}`);
      toast.success("Kesim silindi");
      if (editingId === id) cancelEdit();
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="cutting-page">
      <div className="mb-6">
        <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Kesimhane</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
          <Scissors className="w-7 h-7 text-[#D97706]" /> Kesim Listesi
        </h1>
        <p className="text-[#6B5B4A] mt-2 text-sm">Stok kartı seçin, numara adetlerini girin — kesim otomatik olarak hazır stoğa eklenir</p>
      </div>

      {prefill?.length > 0 && (
        <div className="rounded-lg border border-[#D97706]/50 bg-[#EFE6D6] p-4 mb-6 text-sm" data-testid="cutting-prefill-info">
          {location.state?.source || "Eksik üretim listesinden"} {prefill.length} kalem aktarıldı. Adetleri kontrol edip &ldquo;Stoğa Aktar&rdquo; ile kaydedin.
        </div>
      )}

      {products.length === 0 && (
        <div className="rounded-lg border border-[#F59E0B]/40 bg-[#EFE6D6] p-4 mb-6 text-sm" data-testid="cutting-no-products">
          Kesim girebilmek için önce stok kartı oluşturun.{" "}
          <Link to="/kart" className="text-[#B45309] underline font-semibold">Stok Kartları →</Link>
        </div>
      )}

      <div className={`rounded-lg border bg-[#FFFBF4] p-5 mb-6 ${editingId ? "border-[#D97706]" : "border-[#DCCFB8]"}`} data-testid="cutting-form">
        {editingId && (
          <div className="flex items-center justify-between mb-4 rounded-md bg-[#D97706]/15 border border-[#D97706]/50 px-4 py-2" data-testid="cutting-edit-banner">
            <span className="text-sm font-semibold text-[#B45309]">Kesim düzenleniyor — kaydedince stok ve taban farkı otomatik düzeltilir</span>
            <button onClick={cancelEdit} data-testid="cutting-edit-cancel" className="text-sm text-[#6B5B4A] hover:text-[#2B2118] underline">Vazgeç</button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B5B4A]">Kesici Ustası</Label>
            <Input data-testid="cutting-cutter-input" value={cutterName} onChange={(e) => setCutterName(e.target.value)}
              placeholder="Örn: Mehmet Usta" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B5B4A]">Not</Label>
            <Input data-testid="cutting-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Opsiyonel" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
          </div>
        </div>

        <div className="space-y-3">
        {blocks.map((bl, idx) => (
          <OrderModelBlock key={bl[0].block} idx={idx} lines={bl} prefix="cutting-block"
            usedModels={usedModels[idx]}
            onModel={(m) => acts.setModel(bl[0].block, m)} onUpdate={updateLine} onUpdateSize={updateSize}
            onAddColor={() => acts.addColor(bl[0].block, bl[0].model)} onRemoveLine={acts.removeLine}
            onRemoveBlock={blocks.length > 1 ? () => acts.removeBlock(bl[0].block) : null}
            renderExtra={(line, i) => <SoleWarning w={soleWarningsFor(line)} testId={`cutting-sole-warning-${idx}-${i}`} />} />
        ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-5">
          <Button type="button" variant="outline" onClick={addLine} data-testid="cutting-add-line"
            className="border-[#DCCFB8] bg-[#EFE6D6] hover:bg-[#FFFBF4] text-[#B45309]">
            <Plus className="w-4 h-4 mr-2" /> Model Ekle
          </Button>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Toplam</p>
              <p className="font-mono text-2xl font-bold tabular-nums text-[#B45309]" data-testid="cutting-total">{totalPairs} çift</p>
            </div>
            <Button onClick={submit} disabled={submitting} data-testid="cutting-submit-btn"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              {submitting ? "Kaydediliyor..." : editingId ? "Kaydet ve Yazdır" : "Stoğa Aktar ve Yazdır"}
            </Button>
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid="cutting-history">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4">Kesim Geçmişi</h2>
        {history.length === 0 && <p className="text-sm text-[#8C7B68]">Henüz kesim girişi yok</p>}
        <div className="space-y-3">
          {history.map((c) => {
            const total = c.items.reduce((a, b) => a + b.qty, 0);
            return (
              <div key={c.id} className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-4" data-testid={`cutting-history-${c.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium">{c.cutter_name || "Kesici"}</p>
                    <p className="text-[11px] font-mono text-[#8C7B68] uppercase tracking-widest">{new Date(c.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xl font-bold text-[#B45309]">+{total} çift</span>
                    <a href={`/kesim-fis/${c.id}`} target="_blank" rel="noreferrer" data-testid={`cutting-print-${c.id}`} title="Yazdır"
                      className="text-[#8C7B68] hover:text-[#B45309]"><Printer className="w-4 h-4" /></a>
                    <button onClick={() => startEdit(c)} data-testid={`cutting-edit-${c.id}`} title="Düzenle"
                      className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                    {admin && <>
                    <button onClick={() => removeCutting(c.id)} data-testid={`cutting-delete-${c.id}`} title="Sil"
                      className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                    </>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {c.items.map((it) => (
                    <span key={`${it.product_id}-${it.size}`} className="text-[11px] font-mono px-2 py-1 rounded bg-[#FFFBF4] border border-[#DCCFB8] text-[#6B5B4A]">
                      {productLabel(productById(it.product_id))} · N°{it.size} · <b className="text-[#2B2118]">{it.qty}</b>
                    </span>
                  ))}
                </div>
                {c.note && <p className="text-xs text-[#8C7B68] mt-2 italic">&ldquo;{c.note}&rdquo;</p>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
